(() => {
  if (window.__PMP_TRACKING_EVENTS_LOADED__) {
    return;
  }

  window.__PMP_TRACKING_EVENTS_LOADED__ = true;

  const trackedDetails = new WeakSet();
  const trackedEventIds = new Map();
  const trackedEventTtl = 10000;
  const context = window.pmpTrackingContext || {};
  let currentCart = context.cart || null;
  let consentState = null;
  let viewItemTracked = false;
  let checkoutTrackedAt = 0;

  const canonicalEvents = {
    view_item: {
      legacy: 'pmp_view_item',
      meta: 'ViewContent',
      googleAdsConfig: 'googleAdsViewItemSendTo',
    },
    add_to_cart: {
      legacy: 'pmp_product_added_to_cart',
      meta: 'AddToCart',
      googleAdsConfig: 'googleAdsAddToCartSendTo',
    },
    begin_checkout: {
      legacy: 'pmp_begin_checkout',
      meta: 'InitiateCheckout',
      googleAdsConfig: 'googleAdsBeginCheckoutSendTo',
    },
    purchase: {
      legacy: 'pmp_purchase',
      meta: 'Purchase',
      googleAdsConfig: 'googleAdsPurchaseSendTo',
    },
  };

  const getConfig = () => ({
    dataLayer: false,
    gtag: false,
    meta: false,
    capi: false,
    capiProxyPath: '',
    debug: Boolean(window.__PMP_DEBUG__),
    feedCountry: context.feed_country || window.Shopify?.country || 'US',
    feedIdPattern: 'shopify_{country}_{product_id}_{variant_id}',
    itemGroupIdPattern: 'shopify_{country}_{product_id}',
    googleConsentMode: false,
    metaLimitedDataUse: false,
    dataLayerRequiresConsent: false,
    capiRequiresMarketing: true,
    ...(window.pmpTrackingConfig || {}),
  });

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  };

  const normalizeMoney = (value) => {
    const number = toNumber(value);
    return typeof number === 'number' ? Number(number.toFixed(2)) : undefined;
  };

  const centsToAmount = (value) => {
    const cents = toNumber(value);
    return typeof cents === 'number' ? normalizeMoney(cents / 100) : undefined;
  };

  const replacePattern = (pattern, values) => String(pattern || '').replace(/\{([a-z_]+)\}/g, (_match, key) => (
    values[key] === undefined || values[key] === null ? '' : String(values[key])
  ));

  const getFeedCountry = (config = getConfig()) => (
    config.feedCountry ||
    context.feed_country ||
    window.Shopify?.country ||
    'US'
  );

  const getCurrency = (detail = {}) => (
    detail.currency ||
    currentCart?.currency ||
    context.currency ||
    window.Shopify?.currency?.active ||
    window.pmpTrackingCurrency ||
    ''
  );

  const buildFeedId = (item = {}, config = getConfig()) => {
    if (item.feed_id) {
      return String(item.feed_id);
    }

    const productId = item.product_id || item.item_id || '';
    const variantId = item.variant_id || item.item_variant || item.id || productId;

    if (!productId && !variantId) {
      return '';
    }

    return replacePattern(config.feedIdPattern, {
      country: getFeedCountry(config),
      product_id: productId,
      variant_id: variantId,
      sku: item.sku || '',
      handle: item.handle || '',
    });
  };

  const buildItemGroupId = (item = {}, config = getConfig()) => {
    if (item.item_group_id) {
      return String(item.item_group_id);
    }

    const productId = item.product_id || item.item_id || '';

    if (!productId) {
      return '';
    }

    return replacePattern(config.itemGroupIdPattern, {
      country: getFeedCountry(config),
      product_id: productId,
      variant_id: item.variant_id || item.item_variant || '',
      sku: item.sku || '',
      handle: item.handle || '',
    });
  };

  const createEventId = (eventName, payload) => {
    const item = payload.item || payload.items?.[0] || {};
    const id = item.feed_id || item.item_variant || item.item_id || 'item';
    return `pmp_${eventName}_${id}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  };

  const pruneTrackedEventIds = () => {
    const expiresBefore = Date.now() - trackedEventTtl;

    trackedEventIds.forEach((trackedAt, eventId) => {
      if (trackedAt < expiresBefore) {
        trackedEventIds.delete(eventId);
      }
    });
  };

  const readConsentState = () => {
    const privacy = window.Shopify?.customerPrivacy;

    if (!privacy) {
      return {
        known: false,
        analyticsAllowed: true,
        marketingAllowed: true,
        saleOfDataAllowed: true,
        region: '',
      };
    }

    const call = (name, fallback) => {
      try {
        return typeof privacy[name] === 'function' ? Boolean(privacy[name]()) : fallback;
      } catch (error) {
        return fallback;
      }
    };

    let region = '';

    try {
      region = typeof privacy.getRegion === 'function' ? String(privacy.getRegion() || '') : '';
    } catch (error) {
      region = '';
    }

    return {
      known: true,
      analyticsAllowed: call('analyticsProcessingAllowed', true),
      marketingAllowed: call('marketingAllowed', true),
      saleOfDataAllowed: call('saleOfDataAllowed', true),
      region,
    };
  };

  const applyGoogleConsentMode = (consent) => {
    const config = getConfig();

    if (config.googleConsentMode !== true || typeof window.gtag !== 'function') {
      return;
    }

    window.gtag('consent', 'update', {
      ad_storage: consent.marketingAllowed ? 'granted' : 'denied',
      ad_user_data: consent.marketingAllowed ? 'granted' : 'denied',
      ad_personalization: consent.marketingAllowed ? 'granted' : 'denied',
      analytics_storage: consent.analyticsAllowed ? 'granted' : 'denied',
    });
  };

  const applyMetaDataProcessingOptions = (consent) => {
    const config = getConfig();

    if (config.metaLimitedDataUse !== true || typeof window.fbq !== 'function') {
      return;
    }

    if (consent.saleOfDataAllowed === false || config.metaLimitedDataUse === 'force') {
      window.fbq('dataProcessingOptions', ['LDU'], 0, 0);
      return;
    }

    window.fbq('dataProcessingOptions', [], 0, 0);
  };

  const updateConsentState = () => {
    consentState = readConsentState();
    applyGoogleConsentMode(consentState);
    return consentState;
  };

  const getConsentState = () => consentState || updateConsentState();

  const loadCustomerPrivacyApi = () => {
    if (window.Shopify?.customerPrivacy || typeof window.Shopify?.loadFeatures !== 'function') {
      updateConsentState();
      return;
    }

    window.Shopify.loadFeatures([
      {
        name: 'consent-tracking-api',
        version: '0.1',
      },
    ], () => {
      updateConsentState();
    });
  };

  const normalizeCartAddPayload = (detail = {}) => {
    detail = detail || {};
    const config = getConfig();
    const item = detail.item || {};
    const cartItem = detail.cart_item || {};
    const request = detail.request || {};
    const quantity = toNumber(item.quantity ?? request.quantity) || 1;
    const unitPrice = normalizeMoney(item.price) ?? centsToAmount(cartItem.final_price ?? cartItem.price);
    const productId = String(item.product_id || item.item_id || cartItem.product_id || '');
    const variantId = String(item.variant_id || item.item_variant || cartItem.variant_id || request.variantId || '');
    const feedId = buildFeedId({
      ...item,
      product_id: productId,
      variant_id: variantId,
    }, config);
    const itemGroupId = buildItemGroupId({
      ...item,
      product_id: productId,
      variant_id: variantId,
    }, config);
    const value = normalizeMoney(detail.value) ?? (
      typeof unitPrice === 'number' ? normalizeMoney(unitPrice * quantity) : undefined
    );
    const payload = {
      event: 'add_to_cart',
      legacy_event: 'pmp_product_added_to_cart',
      event_id: detail.event_id || detail.eventId || detail.pmp_event_id || '',
      currency:
        detail.currency ||
        window.Shopify?.currency?.active ||
        window.pmpTrackingCurrency ||
        '',
      value,
      item: {
        item_id: feedId || variantId || productId,
        item_group_id: itemGroupId || productId,
        product_id: productId,
        variant_id: variantId,
        feed_id: feedId,
        item_variant: variantId,
        item_name: item.item_name || cartItem.product_title || cartItem.title || '',
        item_variant_name: item.item_variant_name || cartItem.variant_title || '',
        item_brand: item.item_brand || cartItem.vendor || '',
        sku: item.sku || cartItem.sku || '',
        price: unitPrice,
        quantity,
        url: item.url || cartItem.url || '',
        image_url: item.image_url || cartItem.image || '',
      },
      cart_item: cartItem || null,
      source: detail.source || '',
      form_id: detail.form_id || '',
      request,
    };

    if (!payload.event_id) {
      payload.event_id = createEventId('add_to_cart', payload);
    }

    detail.event_id = payload.event_id;
    return payload;
  };

  const normalizeGenericItem = (raw = {}) => {
    const config = getConfig();
    const productId = String(raw.product_id || raw.productId || raw.item_id || '');
    const variantId = String(raw.variant_id || raw.variantId || raw.item_variant || raw.id || '');
    const quantity = toNumber(raw.quantity) || 1;
    const unitPrice = normalizeMoney(raw.price) ?? centsToAmount(raw.final_price ?? raw.original_price);
    const feedId = buildFeedId({
      ...raw,
      product_id: productId,
      variant_id: variantId,
    }, config);
    const itemGroupId = buildItemGroupId({
      ...raw,
      product_id: productId,
      variant_id: variantId,
    }, config);

    return {
      item_id: feedId || variantId || productId,
      item_group_id: itemGroupId || productId,
      product_id: productId,
      variant_id: variantId,
      feed_id: feedId,
      item_variant: variantId,
      item_name: raw.item_name || raw.product_title || raw.title || raw.name || '',
      item_variant_name: raw.item_variant_name || raw.variant_title || raw.variant || '',
      item_brand: raw.item_brand || raw.vendor || '',
      sku: raw.sku || '',
      price: unitPrice,
      quantity,
      url: raw.url || '',
      image_url: raw.image_url || raw.image || '',
    };
  };

  const normalizeItems = (items = []) => (
    Array.isArray(items) ? items.map(normalizeGenericItem).filter((item) => item.item_id) : []
  );

  const getProductContextItems = () => {
    const product = context.product;

    if (!product) {
      return [];
    }

    const selectedVariantId = String(product.selected_variant_id || '');
    const variant = product.variants?.[selectedVariantId] || Object.values(product.variants || {})[0] || {};

    return [normalizeGenericItem({
      product_id: product.product_id,
      variant_id: variant.variant_id || selectedVariantId,
      title: product.title,
      vendor: product.vendor,
      variant_title: variant.title,
      sku: variant.sku,
      price: centsToAmount(variant.price),
      quantity: 1,
      url: product.url,
      image: product.image,
      handle: product.handle,
    })];
  };

  const getCartItems = (cart = currentCart) => normalizeItems(
    cart?.items?.map((item) => ({
      product_id: item.product_id,
      variant_id: item.variant_id,
      product_title: item.product_title,
      title: item.title,
      variant_title: item.variant_title,
      vendor: item.vendor,
      sku: item.sku,
      final_price: item.final_price,
      price: item.final_price ?? item.price,
      quantity: item.quantity,
      url: item.url,
      image: item.image,
    })) || []
  );

  const normalizeCanonicalPayload = (eventName, detail = {}) => {
    if (eventName === 'add_to_cart') {
      return normalizeCartAddPayload(detail);
    }

    let items = normalizeItems(detail.items);

    if (!items.length && detail.item) {
      items = [normalizeGenericItem(detail.item)];
    }

    if (!items.length && eventName === 'view_item') {
      items = getProductContextItems();
    }

    if (!items.length && eventName === 'begin_checkout') {
      items = getCartItems(detail.cart || currentCart);
    }

    const value = normalizeMoney(detail.value) ?? normalizeMoney(items.reduce((total, item) => (
      total + ((toNumber(item.price) || 0) * (toNumber(item.quantity) || 1))
    ), 0));
    const payload = {
      event: eventName,
      legacy_event: canonicalEvents[eventName]?.legacy || `pmp_${eventName}`,
      event_id: detail.event_id || detail.eventId || detail.pmp_event_id || '',
      currency: getCurrency(detail),
      value,
      item: items[0] || null,
      items,
      source: detail.source || '',
      transaction_id: detail.transaction_id || detail.order_id || '',
      tax: normalizeMoney(detail.tax),
      shipping: normalizeMoney(detail.shipping),
      coupon: detail.coupon || '',
      request: detail.request || {},
    };

    if (!payload.event_id) {
      payload.event_id = createEventId(eventName, payload);
    }

    if (detail && typeof detail === 'object') {
      detail.event_id = payload.event_id;
    }

    return payload;
  };

  const buildGa4Item = (item = {}) => ({
    item_id: item.item_id,
    item_group_id: item.item_group_id || undefined,
    item_name: item.item_name,
    item_variant: item.item_variant_name || item.item_variant || undefined,
    item_brand: item.item_brand || undefined,
    price: item.price,
    quantity: item.quantity,
    sku: item.sku || undefined,
  });

  const buildGa4Event = (payload) => {
    const ecommerce = {
      currency: payload.currency,
      value: payload.value,
      items: (payload.items || [payload.item]).filter(Boolean).map(buildGa4Item),
    };

    if (payload.event === 'purchase') {
      ecommerce.transaction_id = payload.transaction_id || payload.event_id;
      ecommerce.tax = payload.tax;
      ecommerce.shipping = payload.shipping;
      ecommerce.coupon = payload.coupon || undefined;
    }

    return {
      event: payload.event || 'add_to_cart',
      event_id: payload.event_id,
      pmp_event_id: payload.event_id,
      pmp_source: payload.source,
      ecommerce,
      consent: {
        analytics_allowed: getConsentState().analyticsAllowed,
        marketing_allowed: getConsentState().marketingAllowed,
        sale_of_data_allowed: getConsentState().saleOfDataAllowed,
      },
    };
  };

  const buildMetaPayload = (payload) => {
    const items = (payload.items || [payload.item]).filter(Boolean);

    return {
      content_ids: items.map((item) => item.item_id).filter(Boolean),
      content_type: 'product',
      contents: items.map((item) => ({
        id: item.item_id,
        quantity: item.quantity,
        item_price: item.price,
      })),
      currency: payload.currency,
      value: payload.value,
      order_id: payload.transaction_id || undefined,
    };
  };

  const shouldTrack = (detail, payload) => {
    pruneTrackedEventIds();

    if (detail && typeof detail === 'object') {
      if (trackedDetails.has(detail)) {
        return false;
      }

      trackedDetails.add(detail);
    }

    if (payload.event_id && trackedEventIds.has(payload.event_id)) {
      return false;
    }

    if (payload.event_id) {
      trackedEventIds.set(payload.event_id, Date.now());
    }

    return true;
  };

  const pushDataLayerEvents = (payload, ga4Event, config, consent) => {
    if (config.dataLayer === false || (config.dataLayerRequiresConsent && !consent.analyticsAllowed)) {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      ...payload,
      pmp_event_id: payload.event_id,
      event: payload.legacy_event,
    });
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(ga4Event);
  };

  const sendGoogleEvents = (payload, ga4Event, config, consent) => {
    if (config.gtag !== true || typeof window.gtag !== 'function') {
      return;
    }

    if (consent.analyticsAllowed) {
      window.gtag('event', payload.event, {
        event_id: payload.event_id,
        currency: payload.currency,
        value: payload.value,
        items: ga4Event.ecommerce.items,
        transaction_id: ga4Event.ecommerce.transaction_id,
      });
    }

    const sendTo = config[canonicalEvents[payload.event]?.googleAdsConfig];

    if (sendTo && consent.marketingAllowed) {
      window.gtag('event', payload.event, {
        send_to: sendTo,
        event_id: payload.event_id,
        value: payload.value,
        currency: payload.currency,
        items: ga4Event.ecommerce.items,
        transaction_id: ga4Event.ecommerce.transaction_id || payload.event_id,
      });
    }
  };

  const sendMetaEvent = (payload, config, consent) => {
    if (config.meta !== true || !consent.marketingAllowed || typeof window.fbq !== 'function') {
      return;
    }

    applyMetaDataProcessingOptions(consent);
    window.fbq('track', canonicalEvents[payload.event]?.meta || payload.event, buildMetaPayload(payload), {
      eventID: payload.event_id,
    });
  };

  const getCookie = (name) => {
    const escapedName = name.replace(/[$()*+./?[\\\]^{|}-]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  };

  const getAllowedCapiUrl = (config) => {
    if (!config.capiProxyPath || typeof config.capiProxyPath !== 'string') {
      return null;
    }

    let url;

    try {
      url = new URL(config.capiProxyPath, window.location.origin);
    } catch (error) {
      return null;
    }

    if (url.origin !== window.location.origin || !url.pathname.startsWith('/apps/')) {
      return null;
    }

    return url;
  };

  const sendServerEvent = (payload, ga4Event, config, consent) => {
    if (config.capi !== true || (config.capiRequiresMarketing && !consent.marketingAllowed)) {
      return;
    }

    const url = getAllowedCapiUrl(config);

    if (!url) {
      return;
    }

    const body = JSON.stringify({
      event_name: canonicalEvents[payload.event]?.meta || payload.event,
      canonical_event_name: payload.event,
      event_id: payload.event_id,
      event_time: Math.floor(Date.now() / 1000),
      source_url: window.location.href,
      action_source: 'website',
      user_data: {
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
      },
      custom_data: buildMetaPayload(payload),
      ecommerce: ga4Event.ecommerce,
      consent,
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(url.toString(), new Blob([body], { type: 'application/json' }));
      return;
    }

    fetch(url.toString(), {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    }).catch(() => {});
  };

  const publishCanonicalEvent = (eventName, detail = {}, options = {}) => {
    const config = getConfig();
    const consent = getConsentState();
    const payload = normalizeCanonicalPayload(eventName, detail);

    if (!options.allowDuplicate && !shouldTrack(detail, payload)) {
      if (config.debug) {
        console.log('[Tracking] Skipped duplicate event', payload);
      }

      return {
        payload,
        tracked: false,
      };
    }

    const ga4Event = buildGa4Event(payload);

    window.pmpLastTrackingEvent = payload;

    if (payload.event === 'add_to_cart') {
      window.pmpLastCartAddEvent = payload;
    }

    pushDataLayerEvents(payload, ga4Event, config, consent);
    sendGoogleEvents(payload, ga4Event, config, consent);
    sendMetaEvent(payload, config, consent);
    sendServerEvent(payload, ga4Event, config, consent);

    if (config.debug) {
      console.log('[Tracking] Published canonical event', {
        payload,
        ga4Event,
        metaEvent: buildMetaPayload(payload),
        consent,
      });
    }

    return {
      payload,
      ga4Event,
      metaEvent: buildMetaPayload(payload),
      consent,
      tracked: true,
    };
  };

  const trackCartAdd = (detail = {}) => publishCanonicalEvent('add_to_cart', detail);
  const trackViewItem = (detail = {}) => publishCanonicalEvent('view_item', detail, {
    allowDuplicate: !viewItemTracked,
  });
  const trackBeginCheckout = (detail = {}) => publishCanonicalEvent('begin_checkout', detail);
  const trackPurchase = (detail = {}) => publishCanonicalEvent('purchase', detail);

  const onProductAddedToCart = (event) => {
    trackCartAdd(event.detail);
  };

  const onCartChanged = (event) => {
    currentCart = event.detail?.cart || currentCart;
  };

  const bindCheckoutTracking = () => {
    document.addEventListener('click', (event) => {
      const target = event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');

      if (!target) {
        return;
      }

      const now = Date.now();

      if (now - checkoutTrackedAt < 1000) {
        return;
      }

      checkoutTrackedAt = now;
      trackBeginCheckout({
        source: 'checkout-click',
        cart: currentCart,
      });
    }, true);
  };

  const trackInitialViewItem = () => {
    if (viewItemTracked || !context.product) {
      return;
    }

    viewItemTracked = true;
    trackViewItem({
      source: 'product-page',
      currency: context.currency,
    });
  };

  const initialize = () => {
    loadCustomerPrivacyApi();
    bindCheckoutTracking();
    trackInitialViewItem();
  };

  window.PMPTrackingEvents = {
    buildFeedId,
    normalizeCartAddPayload,
    publishCanonicalEvent,
    trackCartAdd,
    trackViewItem,
    trackBeginCheckout,
    trackPurchase,
    updateConsentState,
  };

  window.addEventListener('pmp:product_added_to_cart', onProductAddedToCart);
  document.addEventListener('pmp:product_added_to_cart', onProductAddedToCart);
  window.addEventListener('pmp:cart_changed', onCartChanged);
  document.addEventListener('visitorConsentCollected', updateConsentState);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
